import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  IconButton,
  Box,
  FormHelperText,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import {
  postEmployeeSkills,
  getEmployeeSkills,
  putEmployeeSkills,
  deleteEmployeeSkills,
} from "app/services/Services";
import { useParams } from "react-router-dom";
import {
  generateErrorMessage,
  generateSuccessMessage,
} from "app/utils/GenerateMessage";
import ConfirmModal from "./ConfirmModal";
import {
  T,
  sectionCardSx,
  muiFieldSx,
  SectionHeader,
  StatusChip,
  PrimaryButton,
  SecondaryButton,
  LoadingBlock,
  ErrorBlock,
} from "./shared/formKit";

const skillsSchema = Yup.object().shape({
  skills: Yup.array()
    .of(
      Yup.object().shape({
        skill_name: Yup.string().required("Skill name is required"),
        proficiency: Yup.string()
          .oneOf(["Beginner", "Intermediate", "Advanced", "Expert"])
          .required("Proficiency is required"),
        years_experience: Yup.number()
          .min(0, "Must be at least 0")
          .max(50, "Maximum is 50 years")
          .required("Years of experience is required"),
      })
    )
    .min(1, "At least one skill is required"),
});

const SkillsDetails = ({ onCompleted = null }) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [existingSkills, setExistingSkills] = useState([]);
  const [employeeId] = useState(id);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const isEdit = existingSkills.length > 0;

  useEffect(() => {
    loadExistingSkills();
  }, [id]);

  const loadExistingSkills = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const response = await getEmployeeSkills(id);
      if (response?.status === 200) {
        setExistingSkills(response.data?.data || []);
      } else {
        setExistingSkills([]);
      }
    } catch (err) {
      console.error(err);
      setExistingSkills([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const getInitialValues = () => {
    if (isEdit) {
      return {
        skills: existingSkills.map((s) => ({
          id: s.id,
          skill_name: s.skill_name || "",
          proficiency: s.proficiency || "",
          years_experience: s.years_experience || 0,
        })),
      };
    }
    return { skills: [{ skill_name: "", proficiency: "", years_experience: 0 }] };
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      const updated = values.skills.filter((s) => s.id);
      const created = values.skills.filter((s) => !s.id);

      if (updated.length) {
        await putEmployeeSkills({
          skills: updated.map((s) => ({
            id: s.id,
            skillName: s.skill_name,
            proficiency: s.proficiency,
            yearsExperience: parseInt(s.years_experience) || null,
          })),
        });
      }

      if (created.length) {
        await postEmployeeSkills(employeeId, {
          employeeId,
          skills: created.map((s) => ({
            skillName: s.skill_name,
            proficiency: s.proficiency,
            yearsExperience: parseInt(s.years_experience) || null,
          })),
        });
      }

      generateSuccessMessage("Skills saved successfully");
      await loadExistingSkills();
      if (onCompleted) onCompleted(values.skills);
    } catch (err) {
      console.error(err);
      generateErrorMessage(err.response?.data?.message || "Failed to save skills");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (skillId, removeFromForm) => {
    if (!skillId) {
      removeFromForm();
      return;
    }
    setDeleteTarget({ skillId, removeFromForm });
    setConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const { skillId } = deleteTarget;

    try {
      setLoading(true);
      await deleteEmployeeSkills([skillId]);
      generateSuccessMessage("Record deleted successfully");
      await loadExistingSkills();
      setConfirmModalOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error(err);
      generateErrorMessage(err.response?.data?.message || "Failed to delete skill");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={sectionCardSx}>
        <LoadingBlock label="Loading skills data…" />
      </Box>
    );
  }

  return (
    <Box>
      {loadError && <ErrorBlock message="Failed to load skills data." onRetry={loadExistingSkills} />}

      <Formik
        enableReinitialize
        initialValues={getInitialValues()}
        validationSchema={skillsSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
          <Form>
            <FieldArray name="skills">
              {({ push, remove }) => (
                <Box sx={sectionCardSx}>
                  <SectionHeader
                    title="Skills & competencies"
                    action={
                      <SecondaryButton
                        type="button"
                        onClick={() => push({ skill_name: "", proficiency: "", years_experience: 0 })}
                        disabled={isSubmitting}
                      >
                        <AddIcon sx={{ fontSize: 16, verticalAlign: "middle", mr: "6px" }} />
                        Add skill
                      </SecondaryButton>
                    }
                  />

                  {values.skills.map((skill, index) => (
                    <Box
                      key={`${skill.id || "new"}-${index}`}
                      sx={{ border: `1px solid ${T.cardBorder}`, borderRadius: T.inputR, padding: "16px 18px", mb: "16px" }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <Box sx={{ fontFamily: T.body_f, fontSize: "13.5px", fontWeight: 600, color: T.ink }}>
                            Skill {index + 1}
                          </Box>
                          <StatusChip label={skill.id ? "Existing" : "New"} tone={skill.id ? "neutral" : "warning"} />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(skill.id, () => remove(index))}
                          disabled={isSubmitting || loading}
                          sx={{ color: T.danger }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth size="small" sx={muiFieldSx}
                            name={`skills.${index}.skill_name`} label="Skill name"
                            value={skill.skill_name} onChange={handleChange} onBlur={handleBlur}
                            error={touched.skills?.[index]?.skill_name && Boolean(errors.skills?.[index]?.skill_name)}
                            helperText={touched.skills?.[index]?.skill_name && errors.skills?.[index]?.skill_name}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FormControl
                            fullWidth size="small" sx={muiFieldSx}
                            error={touched.skills?.[index]?.proficiency && Boolean(errors.skills?.[index]?.proficiency)}
                          >
                            <InputLabel>Proficiency level</InputLabel>
                            <Select
                              name={`skills.${index}.proficiency`}
                              value={skill.proficiency}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label="Proficiency level"
                            >
                              <MenuItem value="Beginner">Beginner</MenuItem>
                              <MenuItem value="Intermediate">Intermediate</MenuItem>
                              <MenuItem value="Advanced">Advanced</MenuItem>
                              <MenuItem value="Expert">Expert</MenuItem>
                            </Select>
                            {touched.skills?.[index]?.proficiency && errors.skills?.[index]?.proficiency && (
                              <FormHelperText>{errors.skills[index].proficiency}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth size="small" sx={muiFieldSx}
                            type="number" name={`skills.${index}.years_experience`} label="Years of experience"
                            value={skill.years_experience} onChange={handleChange} onBlur={handleBlur}
                            inputProps={{ min: 0, max: 50 }}
                            error={touched.skills?.[index]?.years_experience && Boolean(errors.skills?.[index]?.years_experience)}
                            helperText={touched.skills?.[index]?.years_experience && errors.skills?.[index]?.years_experience}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}

                  {typeof errors.skills === "string" && (
                    <FormHelperText error>{errors.skills}</FormHelperText>
                  )}

                  <Box mt={2} display="flex" gap={2}>
                    <PrimaryButton disabled={isSubmitting || !values.skills.length}>
                      {isSubmitting ? "Saving…" : "Save / update"}
                    </PrimaryButton>
                    <SecondaryButton type="button" onClick={loadExistingSkills} disabled={isSubmitting}>
                      Refresh
                    </SecondaryButton>
                  </Box>
                </Box>
              )}
            </FieldArray>
          </Form>
        )}
      </Formik>
      <ConfirmModal
        open={confirmModalOpen}
        onClose={() => { setConfirmModalOpen(false); setDeleteTarget(null); }}
        onConfirm={handleConfirmDelete}
        title="Delete Skill Record?"
        description="Are you sure you want to delete this skill? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        loading={loading}
      />
    </Box>
  );
};

export default SkillsDetails;
